/* ============================================================
   Verticals — the service switcher's registry (docs/SERVICE_SWITCHER_PLAN.md §8).

   Two services, fixed. `fence` is today's app, every workspace. `concrete` is
   Rise & Shine: lifting, floor coatings, cleaning, sealing and caulking are
   JOB TYPES inside the one Concrete lens, never switcher entries. Every string
   a shared screen swaps by service (the unit, the noun, the button labels)
   comes from here, and the `fence` entry is pinned by scripts/unit-verticals.mjs
   to the literals those screens used before this existed — so adding a lens
   under the app cannot change a fence screen by accident.

   Who sees the switch (activeVertical): a PLATFORM ADMIN (the FW Admin flag
   from /support/me), on a workspace that runs more than one service
   (workspaces.verticals — server-owned, migration #43), outside the sandbox.
   Everyone else resolves to fence whatever localStorage says, and every lens
   filter is then a no-op. That is the "everyone else only sees Fence Works"
   requirement, and the reason this layer costs the fence app nothing.

   Records (products, quotes, projects, work orders) carry `vertical` in their
   own object (it round-trips through the lossless `data` jsonb). Absent means
   fence: nothing existing is backfilled, and a fence record is never stamped.

   Why "vertical" and not "service": settings.services is already the AI
   receptionist's free-text service list (server/src/db.js loadContractor) and
   leads.service is the lead's ask.

   Plain functions + a bottom export: scripts/unit-verticals.mjs loads them
   without the DOM.
   ============================================================ */

type VerticalId = "fence" | "concrete";
type JobType = "lift" | "coat" | "clean" | "seal" | "caulk";

interface Vertical {
  id: VerticalId;
  label: string;            // the switcher and chips: "Fence" / "Concrete"
  icon: string;             // Icon name
  color: string;            // the chip's colour
  unit: string;             // "ft" / "sq ft" — what a record's measure prints as
  unitLong: string;         // "linear feet" / "square feet"
  unitCol: string;          // the Estimates list column head: "LF" / "Sq ft"
  thing: string;            // "fence" / "concrete" — the noun in a sentence
  things: string;           // "fences" / "concrete jobs"
  newEstimate: string;      // the sidebar's primary button
  estimateVerb: string;     // "Draw the fence" / "Mark the slabs"
  hiddenRoutes: string[];   // fence-only screens hidden under this lens (app.tsx VERTICAL_HIDES)
  jobTypes: Record<string, { label: string; unit: string }> | null;
}

const VERTICAL_IDS: VerticalId[] = ["fence", "concrete"];
const DEFAULT_VERTICAL: VerticalId = "fence";
/* the per-user, per-device lens (localStorage); the server never sees it */
const LENS_KEY = "fw_vertical";
/* fired on window after the lens changes, so screens re-read without a reload */
const LENS_EVENT = "fw:vertical";

const VERTICALS: Record<VerticalId, Vertical> = {
  fence: {
    id: "fence", label: "Fence", icon: "ruler", color: "var(--teal)",
    unit: "ft", unitLong: "linear feet", unitCol: "LF", thing: "fence", things: "fences",
    newEstimate: "New estimate", estimateVerb: "Draw the fence",
    hiddenRoutes: [],
    jobTypes: null,
  },
  concrete: {
    id: "concrete", label: "Concrete", icon: "layers", color: "var(--amber)",
    unit: "sq ft", unitLong: "square feet", unitCol: "Sq ft", thing: "concrete", things: "concrete jobs",
    newEstimate: "New estimate", estimateVerb: "Mark the slabs",
    // fence-only screens: the website builder, the 3D fence, fence
    // codes/permits/specs/models. Lead Radar and the Print Kit carry concrete
    // sources and masters of their own (§13 item 20), so they show under both lenses
    hiddenRoutes: ["website", "visualizer", "codes", "permits", "specs", "models"],
    jobTypes: {
      lift: { label: "Lifting & leveling", unit: "sq ft" },
      coat: { label: "Floor coating", unit: "sq ft" },
      clean: { label: "Cleaning", unit: "sq ft" },
      seal: { label: "Sealing", unit: "sq ft" },
      caulk: { label: "Caulking", unit: "ft" },
    },
  },
};

function isVerticalId(v: any): v is VerticalId {
  return typeof v === "string" && (VERTICAL_IDS as string[]).indexOf(v) > -1;
}

/* a stored list → known ids, deduped, in VERTICAL_IDS order; empty → [fence] */
function normalizeVerticals(list: any): VerticalId[] {
  const set: any = {};
  (Array.isArray(list) ? list : []).forEach((v: any) => { if (isVerticalId(v)) set[v] = true; });
  const out = VERTICAL_IDS.filter((id) => set[id]);
  return out.length ? out : [DEFAULT_VERTICAL];
}

/* The services this workspace runs. The server's answer (/support/me, read off
   workspaces.verticals) wins; the hydrated settings copy is the offline
   fallback; nothing known → fence only. */
function enabledVerticals(settings: any, fromServer?: any): VerticalId[] {
  if (Array.isArray(fromServer) && fromServer.length) return normalizeVerticals(fromServer);
  return normalizeVerticals(settings && settings.verticals);
}

/* may this person switch? admin + more than one service + not the sandbox */
function canSwitch(enabled: VerticalId[], admin: boolean, sandbox: boolean): boolean {
  return !!admin && !sandbox && Array.isArray(enabled) && enabled.length > 1;
}

/* The lens: fence unless this person may switch AND their saved pick is one
   of the enabled services. Pure — the caller passes the saved pick. */
function activeVertical(enabled: VerticalId[], admin: boolean, sandbox: boolean, pref: any): VerticalId {
  if (!canSwitch(enabled, admin, sandbox)) return DEFAULT_VERTICAL;
  return isVerticalId(pref) && enabled.indexOf(pref) > -1 ? pref : DEFAULT_VERTICAL;
}

function readLensPref(): VerticalId | null {
  try { const v = typeof localStorage !== "undefined" ? localStorage.getItem(LENS_KEY) : null; return isVerticalId(v) ? v : null; } catch (e) { return null; }
}
function writeLensPref(v: VerticalId) {
  try { if (typeof localStorage !== "undefined") localStorage.setItem(LENS_KEY, v); } catch (e) {}
  try { if (typeof window !== "undefined") window.dispatchEvent(new Event(LENS_EVENT)); } catch (e) {}
}

/* a record's service; absent or unknown = fence */
function verticalOf(record: any): VerticalId {
  const v = record && record.vertical;
  return isVerticalId(v) ? v : DEFAULT_VERTICAL;
}
function inLens(record: any, lens: any): boolean {
  return verticalOf(record) === (isVerticalId(lens) ? lens : DEFAULT_VERTICAL);
}
/* stamp a NEW record with the lens it was made under — never for fence, so a
   fence record is byte-identical to one made before this existed */
function stampVertical<T>(rec: T, lens: any): T {
  if (!isVerticalId(lens) || lens === DEFAULT_VERTICAL || !rec || typeof rec !== "object") return rec;
  return { ...(rec as any), vertical: lens };
}

/* what a record is measured in: its own measure, else the fence footage */
function measureOf(record: any): { qty: number; unit: string } {
  const m = record && record.measure;
  if (m && typeof m === "object" && isFinite(Number(m.qty))) return { qty: Number(m.qty), unit: String(m.unit || VERTICALS[verticalOf(record)].unit) };
  return { qty: Number(record && record.lf) || 0, unit: VERTICALS[verticalOf(record)].unit };
}
/* "180 ft" / "210 sq ft" */
function fmtMeasure(record: any): string {
  const m = measureOf(record);
  return String(Math.round(m.qty)) + " " + m.unit;   // no separators: exactly what the fence screens printed before
}
/* "$/ft" / "$/sq ft" */
function perUnitLabel(lens: any): string {
  return "$/" + VERTICALS[isVerticalId(lens) ? lens : DEFAULT_VERTICAL].unit;
}

/* the job types on a concrete record (chips on its card) */
function jobTypesOf(record: any): JobType[] {
  const list = record && Array.isArray(record.jobTypes) ? record.jobTypes : [];
  const known = VERTICALS.concrete.jobTypes || {};
  return list.filter((j: any) => typeof j === "string" && known[j]) as JobType[];
}
function jobTypeLabel(jt: any): string {
  const known: any = VERTICALS.concrete.jobTypes || {};
  return known[jt] ? known[jt].label : String(jt || "");
}

/* a fence-only screen under another lens */
function routeHidden(route: any, lens: any): boolean {
  const v = VERTICALS[isVerticalId(lens) ? lens : DEFAULT_VERTICAL];
  return v.hiddenRoutes.indexOf(String(route || "")) > -1;
}

export type { VerticalId, JobType, Vertical };

/* ---- brand per service (plan §5.7) ----
   One business, a brand per service. The LEGAL ENTITY is the company:
   company.legalName ("Kodiak Built LLC"), company.license, the company's
   address. A service can TRADE under its own name, logo, phone, email,
   review link and domain — settings.brands[vertical] — each field falling
   back to the company's, so a workspace with no brand card reads exactly as
   it always has. Everything a customer sees goes through brandOf(); the
   contract and the invoice print `legalForm`: "Kodiak Built LLC d/b/a Rise &
   Shine Concrete" when the card says the name is a DBA, else the legal name,
   else the trading name. The settings mirrors (companyName, companyPhone,
   companyLegalName, companyLicense — Settings writes them beside the company
   record) let a caller holding only `settings` — customerDocOf, the hub
   payload — resolve the same brand. server/src/verticals.js mirrors this. */
interface Brand {
  vertical: VerticalId;
  name: string; logo: string; phone: string; email: string; address: string; website: string; reviewUrl: string; domain: string;
  legalName: string; license: string; dba: boolean; legalForm: string;
  /** no brand card for this service: this is just the company */
  isCompany: boolean;
}
const str = (v: any) => (typeof v === "string" ? v.trim() : "");
function legalFormOf(name: any, legalName: any, dba: any): string {
  const n = str(name), l = str(legalName);
  if (l && n && dba && l.toLowerCase() !== n.toLowerCase()) return l + " d/b/a " + n;
  return l || n;
}
function brandOf(src: any, vertical: any): Brand {
  const v: VerticalId = isVerticalId(vertical) ? vertical : DEFAULT_VERTICAL;
  const company = src && src.company && typeof src.company === "object" ? src.company : {};
  const settings = src && src.settings && typeof src.settings === "object" ? src.settings : {};
  const cards = settings.brands && typeof settings.brands === "object" ? settings.brands : {};
  const b = cards[v] && typeof cards[v] === "object" ? cards[v] : null;
  const own = (k: string) => (b ? str(b[k]) : "");
  const name = own("name") || str(company.name) || str(settings.companyName);
  const legalName = str(company.legalName) || str(settings.companyLegalName);
  const dba = !!(b && b.dba) && !!legalName;
  return {
    vertical: v,
    name,
    logo: own("logo") || str(company.logo),
    phone: own("phone") || str(company.phone) || str(settings.companyPhone),
    email: own("email") || str(company.email) || str(settings.companyEmail),
    address: str(company.address) || str(settings.companyAddress),
    website: own("domain") || str(company.website) || str(settings.companyWebsite),
    reviewUrl: own("reviewUrl") || str(settings.googleReviewUrl),
    domain: own("domain"),
    legalName,
    license: str(company.license) || str(settings.companyLicense),
    dba,
    legalForm: legalFormOf(name, legalName, dba),
    isCompany: !b,
  };
}

/* per-service defaults (plan §12 f, g): the deposit % a new quote of this
   service starts with, and its sales tax rate. null = the workspace setting. */
function serviceDefaultsOf(settings: any, vertical: any): { depositPct: number | null; taxRate: number | null } {
  const v = isVerticalId(vertical) ? vertical : DEFAULT_VERTICAL;
  const all = settings && settings.serviceDefaults && typeof settings.serviceDefaults === "object" ? settings.serviceDefaults : {};
  const d = all[v] && typeof all[v] === "object" ? all[v] : {};
  const n = (x: any) => (x == null || x === "" || !isFinite(Number(x)) ? null : Number(x));
  return { depositPct: n(d.depositPct), taxRate: n(d.taxRate) };
}

/* which site checks a service's job carries (plan §5.4, §12 d): a fence post
   hole is an excavation — 811 locates, and often a permit. Foam injection
   drills ⅝″ holes through the slab and a coating never breaks ground, so a
   concrete job carries neither. */
/* the deposit a NEW quote of this service starts with (§13 item 16): one
   number per service, or one per job type under it — a lift is paid on
   completion (0) while a coating takes a deposit. A mixed quote takes the
   largest. null = the workspace's own deposit policy. Mirrored inline by
   lib/quotedetails.ts withQuoteDefaults (import-free) and by the service's
   draft for a website request (server/src/concretequote.js). */
function depositPctFor(settings: any, record: any): number | null {
  const v = verticalOf(record);
  const all = settings && settings.serviceDefaults && typeof settings.serviceDefaults === "object" ? settings.serviceDefaults : {};
  const d = all[v] && typeof all[v] === "object" ? all[v] : {};
  const n = (x: any) => (x == null || x === "" || !isFinite(Number(x)) ? null : Math.max(0, Math.min(100, Number(x))));
  const by = d.depositByJobType && typeof d.depositByJobType === "object" ? d.depositByJobType : null;
  let best: number | null = null;
  if (by) jobTypesOf(record).forEach((jt) => { const x = n(by[jt]); if (x != null) best = best == null ? x : Math.max(best, x); });
  return best != null ? best : n(d.depositPct);
}

function locatesRequired(record: any): boolean { return verticalOf(record) !== "concrete"; }
function permitRequired(record: any): boolean { return verticalOf(record) !== "concrete"; }

export { VERTICAL_IDS, VERTICALS, DEFAULT_VERTICAL, LENS_KEY, LENS_EVENT, isVerticalId, normalizeVerticals, enabledVerticals, canSwitch, activeVertical, readLensPref, writeLensPref, verticalOf, inLens, stampVertical, measureOf, fmtMeasure, perUnitLabel, jobTypesOf, jobTypeLabel, routeHidden, legalFormOf, brandOf, serviceDefaultsOf, depositPctFor, locatesRequired, permitRequired };
export type { Brand };
